import apiClient from './apiClient';

export interface BrowserInfo {
  user_agent: string;
  browser: string;
  version: string;
  os: string;
}

export interface ErrorLog {
  id: string;
  error_type: string;
  error_message: string;
  request_path: string | null;
  request_method: string | null;
  status_code: number | null;
  date: string; // Date in YYYY-MM-DD format
  resolved: boolean;
  resolved_at: string | null;
  resolved_by: string | null;
  notes: string | null;
  count: number; // Number of occurrences
  first_occurrence: string; // ISO timestamp
  last_occurrence: string; // ISO timestamp
  affected_user_ids: string[]; // Array of unique user IDs
  affected_user_emails: string[]; // Array of user emails corresponding to affected_user_ids
  affected_user_count: number; // Count of unique users
  sample_stack_trace: string | null;
  // Frontend-specific fields
  error_source: 'backend' | 'frontend';
  browser_info: BrowserInfo | null;
  component_name: string | null;
  page_url: string | null;
}

export interface ErrorLogsResponse {
  errors: ErrorLog[];
  total: number;
  limit: number;
  offset: number;
}

export interface ErrorStatistics {
  errors_by_type: { [key: string]: number };
  total_errors: number;
  resolved_errors: number;
  unresolved_errors: number;
}

export interface GetErrorsParams {
  user_id?: string;
  error_type?: string;
  resolved?: boolean;
  start_date?: string;
  end_date?: string;
  user_email_search?: string;
  error_source?: 'backend' | 'frontend';
  limit?: number;
  offset?: number;
}

export const adminErrorService = {
  /**
   * Get error logs with optional filtering
   */
  getErrors: async (params?: GetErrorsParams): Promise<ErrorLogsResponse> => {
    const response = await apiClient.get('/payment/admin/errors', { params });
    return response.data.data;
  },

  /**
   * Get detailed information about a specific error
   */
  getErrorDetails: async (errorId: string): Promise<ErrorLog> => {
    const response = await apiClient.get(`/payment/admin/errors/${errorId}`);
    return response.data.data;
  },

  /**
   * Mark an error as resolved
   */
  resolveError: async (errorId: string, notes?: string): Promise<ErrorLog> => {
    const response = await apiClient.post(`/payment/admin/errors/${errorId}/resolve`, {
      notes: notes || null
    });
    return response.data.data;
  },

  /**
   * Mark multiple errors as resolved in bulk
   */
  bulkResolveErrors: async (errorIds: string[], notes?: string): Promise<{ resolved_count: number; not_found_count: number; not_found_ids: string[] }> => {
    const response = await apiClient.post('/payment/admin/errors/bulk-resolve', {
      error_ids: errorIds,
      notes: notes || null
    });
    return response.data.data;
  },

  /**
   * Get error statistics
   */
  getErrorStatistics: async (startDate?: string, endDate?: string, errorSource?: 'backend' | 'frontend'): Promise<ErrorStatistics> => {
    const params: any = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (errorSource) params.error_source = errorSource;

    const response = await apiClient.get('/payment/admin/errors/statistics', { params });
    return response.data.data;
  },
};

